import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { notification, Upload, type UploadProps } from "antd";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@rumble-it/common-components/components";
import { Button } from "@rumble-it/common-components/components";
import { getDossier } from "@/lib/queries/getDossier";
import { getAffiliateId } from "@/lib/queries/client";
import { LoadingState } from "@/components/loading-state";
import { DossierSummaryCard } from "@/components/calculation/dossier-summary-card";

const { Dragger } = Upload;

/**
 * Demo-only upload flow for service partners to submit an Audatex calculation.
 *
 * This is a mocked demo for the RumbleClaims extension: the uploaded file is
 * read and validated in the browser, but nothing is sent to the backend. No
 * calculation is persisted, and the admin Claims module is not affected.
 */
export const CalculationUpload = ({ dossierId }: { dossierId: string }) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const uploadTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (uploadTimeoutRef.current !== null) {
        window.clearTimeout(uploadTimeoutRef.current);
      }
    };
  }, []);

  const { data, isPending, isError } = useQuery({
    queryKey: ["dossier", getAffiliateId(), dossierId],
    queryFn: () => getDossier({ dossierId }),
    enabled: !!dossierId,
  });

  if (!dossierId) {
    return (
      <LoadingState
        isPending={false}
        isError={true}
        errorMessage="Er ontbreekt een dossier ID in de link. Vraag een nieuwe link aan."
      />
    );
  }

  if (isPending || isError) {
    return (
      <LoadingState
        isPending={isPending}
        isError={isError}
        loadingMessage="Bezig met het laden van het dossier..."
      />
    );
  }

  const dossier = data.dossier;

  const handleBeforeUpload: UploadProps["beforeUpload"] = (file) => {
    const isTextFile =
      file.type === "text/plain" || file.name.toLowerCase().endsWith(".txt");

    if (!isTextFile) {
      notification.error({
        message: "Ongeldig bestandstype",
        description: "Upload een Audatex calculatie als .txt bestand.",
      });
      return Upload.LIST_IGNORE;
    }

    setIsUploading(true);

    file
      .text()
      .then((text) => {
        if (!text.trim()) {
          notification.error({
            message: "Leeg bestand",
            description: "Het bestand bevat geen inhoud.",
          });
          setIsUploading(false);
          return;
        }

        // Simulate a short upload delay so the demo feels realistic.
        uploadTimeoutRef.current = window.setTimeout(() => {
          setFileName(file.name);
          setPreview(text.slice(0, 1000));
          setIsUploading(false);
          setIsUploaded(true);

          notification.success({
            message: "Calculatie succesvol geüpload",
            description: `${file.name} is ontvangen.`,
          });
        }, 600);
      })
      .catch(() => {
        setIsUploading(false);
        notification.error({
          message: "Uploaden mislukt",
          description: "Het bestand kon niet worden gelezen.",
        });
      });

    return false;
  };

  const handleReset = () => {
    setFileName(null);
    setPreview(null);
    setIsUploaded(false);
  };

  return (
    <>
      <DossierSummaryCard dossier={dossier} />

      <Card className="mt-5">
        <CardHeader>
          <CardTitle>Calculatie uploaden</CardTitle>
        </CardHeader>

        <CardContent>
          {isUploaded ? (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 rounded-md border border-neutral-200 p-4">
                <span className="text-2xl text-orange-500" aria-hidden="true">
                  📄
                </span>
                <div>
                  <div className="font-bold">{fileName}</div>
                  <div className="text-neutral-500">
                    Calculatie ontvangen. Bedankt voor het uploaden.
                  </div>
                </div>
              </div>

              {preview ? (
                <div>
                  <h4 className="font-bold mb-2">Voorbeeld</h4>
                  <pre className="max-h-64 overflow-auto rounded-md bg-neutral-50 border border-neutral-200 p-4 text-xs whitespace-pre-wrap">
                    {preview}
                  </pre>
                </div>
              ) : null}

              <Button type="button" variant="greyLight" onClick={handleReset}>
                Andere calculatie uploaden
              </Button>
            </div>
          ) : (
            <Dragger
              multiple={false}
              showUploadList={false}
              accept=".txt,text/plain"
              beforeUpload={handleBeforeUpload}
              disabled={isUploading}
            >
              <p className="ant-upload-drag-icon" aria-hidden="true">
                📤
              </p>
              <p className="ant-upload-text">
                {isUploading
                  ? "Bezig met verwerken..."
                  : "Klik of sleep de Audatex calculatie (.txt) hierheen om te uploaden"}
              </p>
              <p className="ant-upload-hint text-neutral-500">
                Alleen .txt bestanden worden geaccepteerd.
              </p>
            </Dragger>
          )}
        </CardContent>
      </Card>
    </>
  );
};
